import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocaleService } from 'src/app/services/locale';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-report-preview',
  imports: [CommonModule],
  templateUrl: './report-preview.component.html',
  styles: [`
    h1, h2, h3 {
      color: #043B22;
    }

    table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 12px;
      page-break-inside: auto;
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid #6B9B7A;
    }

    thead {
      display: table-header-group;
    }

    thead tr {
      background-color: #6B9B7A;
    }

    thead th {
      color: #ffffff;
      font-weight: 600;
      padding: 10px 8px;
      border: none;
      border-bottom: 1px solid #5a8a69;
    }

    tbody {
      display: table-row-group;
    }

    tbody tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    tbody tr:nth-child(odd) {
      background-color: #ffffff;
    }

    tbody tr:nth-child(even) {
      background-color: #f0f7f2;
    }

    tbody td {
      padding: 8px;
      border: none;
      border-bottom: 1px solid #e0e0e0;
      color: #333333;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    /* First and last cell border radius */
    thead tr th:first-child {
      border-top-left-radius: 8px;
    }

    thead tr th:last-child {
      border-top-right-radius: 8px;
    }

    tbody tr:last-child td:first-child {
      border-bottom-left-radius: 8px;
    }

    tbody tr:last-child td:last-child {
      border-bottom-right-radius: 8px;
    }

    /* Section wrapper - each section starts on a new page */
    .section-wrapper {
      page-break-before: always;
      break-before: page;
      padding: 10px !important;
      background: #ffffff;
      position: relative;
      overflow: visible;
    }

    /* Section title with decorative side lines */
    .section-title-container {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 15px;
      page-break-after: avoid;
      break-after: avoid;
    }

    .section-title-line {
      flex: 1;
      /* max-width: 200px; */
      height: 3px;
      background-color: #76AA84;
    }

    .section-title-text {
      display: inline-block;
      color: #000000;
      font-size: 22px;
      font-weight: bold;
      padding: 5px 15px;
      white-space: nowrap;
    }

    /* Subsection heading - avoid breaking */
    .subsection-title {
      color: #043B22;
      font-size: 16px;
      font-weight: 600;
      margin-top: 24px;
      margin-bottom: 12px;
      padding-left: 16px;
      border-left: 4px solid #6B9B7A;
      page-break-after: avoid;
      break-after: avoid;
    }

    /* Paragraph styling - allow breaking for long content */
    .section-paragraph {
      font-size: 13px;
      line-height: 1.8;
      margin-bottom: 12px;
      text-align: justify;
    }


    .index-wrapper h2 {
      color: #043B22;
      font-size: 18px;
      font-weight: 700;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 2px solid #043B22;
    }

    .index-wrapper h3 {
      color: #1a472a;
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 8px;
    }

    .index-wrapper ul {
      margin-bottom: 12px;
      padding-left: 20px;
    }

    .index-wrapper ul li {
      color: #4a5568;
      font-size: 12px;
      line-height: 1.6;
      margin-bottom: 4px;
    }

    /* Page header image - top right corner */
    .page-header-logo {
      position: absolute;
      top: 15px;
      right: 15px;
      width: 67px;
      height: 57px;
      object-fit: contain;
    }

    /* Page footer image - bottom left corner */
    .page-footer-logo {
      position: absolute;
      bottom: 15px;
      left: 15px;
      width: 80px;
      height: auto;
      object-fit: contain;
    }

    /* Allow content to flow to next page */
    .section-content {
      overflow: visible;
    }
    `]
})
export class ReportPreviewComponent implements OnInit {
  currentLogo: string = environment.imagesBaseUrl + '/foodprint-logo.svg';
  imagesBaseUrl = environment.imagesBaseUrl;
  private localeService = inject(LocaleService);

  @Input() companyDetails!: any;
  @Input() locale!: any;
  @Input() sections: ReportSection[] = [];

  ngOnInit(): void {
    const lang = this.localeService.getLanguage() || 'english';
    const key = `preview_report_sections_${lang}`;
    this.companyDetails = JSON.parse(localStorage.getItem("companyDetails") ?? "{}");
    const lsSetLogo = localStorage.getItem('setLogo');
    const lsCompanyLogo = localStorage.getItem('companyLogo');
    const lsProfilePhoto = localStorage.getItem('profilePhoto');
    const valid = (val: string | null) => val && val !== 'null' && val !== 'undefined' && val !== '';

    // Helper to ensure full URL path
    const ensureFullPath = (path: string): string => {
      if (!path) return environment.imagesBaseUrl + '/foodprint-logo.svg';
      if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
        return path;
      }
      // If relative path, prepend base URL
      return environment.baseUrl + (path.startsWith('/') ? path : '/' + path);
    };

    const logoPath = valid(lsSetLogo) ? lsSetLogo! :
      valid(lsCompanyLogo) ? lsCompanyLogo! :
        valid(lsProfilePhoto) ? lsProfilePhoto! :
          valid(this.companyDetails?.companyLogo) ? this.companyDetails.companyLogo :
            valid(this.companyDetails?.profilePhoto) ? this.companyDetails.profilePhoto :
              environment.imagesBaseUrl + '/foodprint-logo.svg';

    this.currentLogo = ensureFullPath(logoPath);

    const stored = localStorage.getItem(key);
    // console.log('Preview ngOnInit at:', Date.now(), 'key:', key);
    if (!stored) {
      // console.log('No stored sections found');
      return;
    }

    const rawSections = JSON.parse(stored);
    // console.log('Raw sections loaded:', rawSections.length);

    // Check if section 7 has images
    const section7 = rawSections.find((s: any) => s.id === 7);
    if (section7) {
      // console.log('Section 7 content contains img:', section7.content?.includes('<img'));
      // console.log('Section 7 content contains canvas:', section7.content?.includes('<canvas'));
      // Log first 500 chars of section 7 content to see what's there
      // console.log('Section 7 content preview:', section7.content?.substring(0, 500));
    }

    this.sections = rawSections.map((s: any) => {
      const blocks = this.parseHtmlToBlocks(s.content);
      if (s.id === 7) {
        // console.log(`Section 7 parsed into ${blocks.length} blocks, types:`, blocks.map(b => b.type));
        // Check for img blocks
        const imgBlocks = blocks.filter(b => b.type === 'img');
        // console.log('Section 7 img blocks:', imgBlocks.length);
      }
      return {
        id: s.id,
        title: s.title,
        blocks
      };
    });
  }

  private parseHtmlToBlocks(html: string): SectionBlock[] {
    const blocks: SectionBlock[] = [];
    if (!html) return blocks;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    // console.log("html => ", html);
    // 👉 Walk in document order
    const walker = (node: Node) => {
      if (!(node instanceof HTMLElement)) return;

      // PARAGRAPH
      if (node.tagName === 'P') {
        const html = node.innerHTML?.trim();
        if (html) {
          blocks.push({ type: 'p', text: html, currentClass: node.className, currentStyle: node.style.cssText });
        }
        return;
      }

      // HEADINGS (H1-H6)
      if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(node.tagName)) {
        const text = node.innerHTML?.trim();
        if (text) {
          blocks.push({
            type: 'heading',
            level: parseInt(node.tagName.charAt(1)),
            text,
            currentClass: node.className,
            currentStyle: node.style.cssText
          });
        }
        return;
      }

      // TABLE
      if (node.tagName === 'TABLE') {
        const headers: { text: string; className: string; style: string }[] = [];
        const rows: string[][] = [];

        node.querySelectorAll('thead th').forEach(th => {
          const thElement = th as HTMLElement;
          headers.push({
            text: th.textContent?.trim() || '',
            className: thElement.className || '',
            style: thElement.style.cssText || ''
          });
        });

        node.querySelectorAll('tbody tr').forEach(tr => {
          const row: string[] = [];
          tr.querySelectorAll('td').forEach(td => {
            row.push(this.extractCellValue(td as HTMLTableCellElement));
          });
          if (row.length) rows.push(row);
        });

        blocks.push({
          type: 'table',
          headers,
          rows,
          currentClass: node.className,
          currentStyle: node.style.cssText
        });
        return;
      }

      // IMAGE
      if (node.tagName === 'IMG') {
        const src = (node as HTMLImageElement).src;
        if (src) {
          blocks.push({
            type: 'img',
            src,
            alt: (node as HTMLImageElement).alt || '',
            currentClass: node.className,
            currentStyle: node.style.cssText
          });
        }
        return;
      }

      // DIV - check for images inside or recurse
      if (node.tagName === 'DIV') {
        const img = node.querySelector('img');
        if (img) {
          blocks.push({
            type: 'img',
            src: img.src,
            alt: img.alt || '',
            currentClass: node.className,
            currentStyle: node.style.cssText
          });
          return;
        }
        // If no img, recurse into children
        node.childNodes.forEach(child => walker(child));
        return;
      }

      // FIGURE (CKEditor wraps images in figure.image, tables in figure.table)
      if (node.tagName === 'FIGURE') {
        const table = node.querySelector('table');
        if (table) {
          // figure.table — delegate to the TABLE handler
          walker(table);
          return;
        }
        const img = node.querySelector('img');
        if (img) {
          blocks.push({
            type: 'img',
            src: img.src,
            alt: img.alt || '',
            currentClass: node.className,
            currentStyle: node.style.cssText
          });
        }
        return;
      }

      // UNORDERED LIST
      if (node.tagName === 'UL') {
        const items: string[] = [];
        node.querySelectorAll('li').forEach(li => {
          items.push(li.innerHTML?.trim() || '');
        });
        if (items.length) {
          blocks.push({
            type: 'ul',
            items,
            currentClass: node.className,
            currentStyle: node.style.cssText
          });
        }
        return;
      }

      // ORDERED LIST
      if (node.tagName === 'OL') {
        const items: string[] = [];
        node.querySelectorAll('li').forEach(li => {
          items.push(li.innerHTML?.trim() || '');
        });
        if (items.length) {
          blocks.push({
            type: 'ol',
            items,
            currentClass: node.className,
            currentStyle: node.style.cssText
          });
        }
        return;
      }

      // INPUT FIELD VALUE
      // TABLE
      if (node.tagName === 'TABLE') {
        const headers: { text: string; className: string; style: string }[] = [];
        const rows: string[][] = [];

        node.querySelectorAll('thead th').forEach(th => {
          const thElement = th as HTMLElement;
          headers.push({
            text: th.textContent?.trim() || '',
            className: thElement.className || '',
            style: thElement.style.cssText || ''
          });
        });

        node.querySelectorAll('tbody tr').forEach(tr => {
          const row: string[] = [];
          tr.querySelectorAll('td').forEach(td => {
            row.push(this.extractCellValue(td as HTMLTableCellElement));
          });
          if (row.length) rows.push(row);
        });

        blocks.push({
          type: 'table',
          headers,
          rows,
          currentClass: node.className,
          currentStyle: node.style.cssText
        });
        return;
      }


      node.childNodes.forEach(child => walker(child));
    };

    walker(doc.body);
    return blocks;
  }

  private extractCellValue(td: HTMLTableCellElement): string {
    // 1️⃣ Input / textarea value
    const input =
      td.querySelector('input, textarea') as
      | HTMLInputElement
      | HTMLTextAreaElement
      | null;

    if (input) {
      return input.value?.trim() || '';
    }

    // 2️⃣ Fallback: normal text
    return td.textContent?.trim() || '';
  }


}

export type SectionBlock =
  | { type: 'p'; text: string; currentClass: string, currentStyle: string }
  | { type: 'heading'; level: number; text: string; currentClass: string, currentStyle: string }
  | { type: 'table'; headers: { text: string; className: string; style: string }[]; rows: string[][]; currentClass: string, currentStyle: string }
  | { type: 'ul'; items: string[]; currentClass: string, currentStyle: string }
  | { type: 'ol'; items: string[]; currentClass: string, currentStyle: string }
  | { type: 'img'; src: string; alt: string; currentClass: string, currentStyle: string };

export interface ReportSection {
  id: number;
  title: string;
  blocks: SectionBlock[];
}

